import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import apiManagerApi from './api';

export type CatalogConnector = {
  provider_key: string;
  display_name: string;
  vendor: string;
  auth_type: string | null;
  auth_status: string;
  docs_url: string;
  connector: string | null;
  endpoints: Array<{ endpoint_id: string; method: string; description?: string }>;
  auth_setup_instructions: string[];
  honesty_note?: string | null;
};

type Props = {
  /** Called with the external API id once a connector is installed (or already was). */
  onInstalled: (apiId: string) => void;
};

/**
 * Prebuilt provider connectors from the shared catalog. Install registers the
 * external API with the catalog's endpoints + field mappings; the farmer then
 * only adds credentials and switches sync on in the workspace.
 */
export default function ConnectorCatalog({ onInstalled }: Props) {
  const [connectors, setConnectors] = useState<CatalogConnector[]>([]);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiManagerApi.listConnectorCatalog()
      .then((resp) => { if (!cancelled) setConnectors(resp.data?.connectors || []); })
      .catch((err: any) => { if (!cancelled) setError(err.response?.data?.error || err.message || 'Failed to load connectors'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const install = async (providerKey: string) => {
    try {
      setInstalling(providerKey);
      setError(null);
      const resp = await apiManagerApi.installConnector(providerKey);
      if (resp.data?.api_id) onInstalled(resp.data.api_id);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Install failed');
    } finally {
      setInstalling(null);
    }
  };

  if (loading) return <CircularProgress size={20} />;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
        {connectors.map((c) => (
            <Card variant="outlined" sx={{ height: '100%' }} key={c.provider_key}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>{c.display_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.vendor}</Typography>
                  </Box>
                  <Chip size="small" label={c.auth_type || 'auth?'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {c.endpoints.map((e) => e.endpoint_id).join(', ')}
                </Typography>
                {c.honesty_note && (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    {c.honesty_note}
                  </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={Boolean(installing)}
                    onClick={() => install(c.provider_key)}
                    startIcon={installing === c.provider_key ? <CircularProgress size={14} color="inherit" /> : undefined}
                  >
                    {installing === c.provider_key ? 'Installing…' : 'Install'}
                  </Button>
                  <Link href={c.docs_url} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    Docs <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                  {c.auth_type === 'oauth2' && (
                    <Typography variant="caption" color="text.secondary">Needs an approved provider OAuth app</Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
        ))}
      </Box>
    </Box>
  );
}
